import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { buttonStyles } from '../css/commonStyles';
import {
  fieldBlockStyles,
  fieldLabelStyles,
  disabledFieldSx,
  managementBoxSx,
  saveButtonSx,
  colors,
} from '../css/revenueItems.styles';
import { updateDaoVaultAddress } from 'helpers/paymentRequest.api';
import { formatRevenueSharingErrorMessage } from '../utils/revenueSharing.utils';
import { ensureRevenueSharingMessageLayer, revenueSharingMessage } from '../utils/revenueSharingMessage';
import RevenueSplitterManagement from './RevenueSplitterManagement';
import OperatorTeamSection from './OperatorTeamSection';
import { isValidVaultAddress } from '../utils/vaultRoles.utils';

const ManagementTab = ({
  currentDao,
  onSaveSuccess,
  isVaultAdmin,
  isOrganizer,
  hasOnChainAccess,
  hasVault,
  isSplitterOperator,
  isVaultOperator,
  splitterAddress,
  operatorRows,
  isSplitterAdmin,
  onRolesRefresh,
  rolesLoading,
}) => {
  const canEditVault = isOrganizer;
  const vaultConfigured = hasVault ?? isValidVaultAddress(currentDao?.vaultAddress);
  const showOrganizerRoleNotice =
    isOrganizer && !rolesLoading && !hasOnChainAccess && vaultConfigured;
  const [vaultAddress, setVaultAddress] = useState(currentDao?.vaultAddress ?? '');
  const [isUpdatingVault, setIsUpdatingVault] = useState(false);

  useEffect(() => {
    ensureRevenueSharingMessageLayer();
  }, []);

  useEffect(() => {
    setVaultAddress(currentDao?.vaultAddress ?? '');
  }, [currentDao?.vaultAddress]);

  const handleSave = async () => {
    if (!canEditVault) {
      revenueSharingMessage.error('Connect the DAO organizer wallet to update the vault address.');
      return;
    }
    if (!vaultAddress.trim()) {
      revenueSharingMessage.error('Vault address is required.');
      return;
    }
    if (!/^0x[0-9a-fA-F]{40}$/.test(vaultAddress.trim())) {
      revenueSharingMessage.error('Invalid wallet address. Must be a valid Ethereum address (0x...).');
      return;
    }
    try {
      setIsUpdatingVault(true);
      await updateDaoVaultAddress(currentDao?._id, vaultAddress.trim());
      revenueSharingMessage.success('Vault address updated successfully.');
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      revenueSharingMessage.error(
        formatRevenueSharingErrorMessage(err, 'Failed to update vault address.'),
      );
    } finally {
      setIsUpdatingVault(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={managementBoxSx}>
        <Box sx={fieldBlockStyles}>
          <Typography sx={fieldLabelStyles}>Vault Address</Typography>
          <TextField
            placeholder="0x..."
            value={vaultAddress}
            onChange={(e) => setVaultAddress(e.target.value)}
            fullWidth
            disabled={!canEditVault}
            sx={disabledFieldSx}
          />
          {!canEditVault && (
            <Typography sx={{ color: colors.textSecondary, mt: 1, fontSize: '0.85rem' }}>
              Only the DAO organizer wallet can change Vault address.
            </Typography>
          )}
        </Box>

        {canEditVault && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isUpdatingVault}
            sx={{ ...buttonStyles, ...saveButtonSx }}
          >
            {isUpdatingVault ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        )}
      </Box>

      {vaultConfigured && (
        <OperatorTeamSection
          vaultAddress={vaultAddress}
          chainId={currentDao?.chainId}
          splitterAddress={splitterAddress}
          operatorRows={operatorRows}
          isVaultAdmin={isVaultAdmin}
          isSplitterAdmin={isSplitterAdmin}
          onUpdated={onRolesRefresh}
          rolesLoading={rolesLoading}
        />
      )}

      {isSplitterOperator && (
        <RevenueSplitterManagement vaultAddress={vaultAddress} chainId={currentDao?.chainId} />
      )}

      {!rolesLoading && !isSplitterOperator && (isVaultOperator || isVaultAdmin) && (
        <Typography sx={{ color: colors.textSecondary, mt: 3, fontFamily: 'Questrial, sans-serif' }}>
          Revenue Splitter configuration requires DAO_OPERATOR on the Revenue Splitter contract. Ask an admin to
          grant your wallet on the Splitter, or use the Operator team section above.
        </Typography>
      )}

      {showOrganizerRoleNotice && (
        <Typography
          sx={{
            color: colors.textSecondary,
            fontFamily: 'Questrial, sans-serif',
            mt: 3,
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}
        >
          Organizer wallet: no ADMIN or DAO_OPERATOR on the Vault contracts. Payment and revenue sharing need
          Vault DAO_OPERATOR.
        </Typography>
      )}
    </Box>
  );
};

export default ManagementTab;
